import React, { useState, useEffect, useRef } from "react";

const SECTIONS = [
  { id: "qui", label: "01", title: "Qui sommes-nous" },
  { id: "collecte", label: "02", title: "Données collectées" },
  { id: "usage", label: "03", title: "Pourquoi ces données" },
  { id: "google", label: "04", title: "Données Google" },
  { id: "acces", label: "05", title: "Qui y a accès" },
  { id: "partage", label: "06", title: "Partage des données" },
  { id: "securite", label: "07", title: "Sécurité" },
  { id: "conservation", label: "08", title: "Conservation" },
  { id: "droits", label: "09", title: "Vos droits" },
  { id: "contact", label: "10", title: "Contact" },
];

export default function PolitiqueConfidentialite() {
  const [active, setActive] = useState("qui");
  const [navOpen, setNavOpen] = useState(false);
  const refs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 }
    );
    Object.values(refs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const jump = (id) => {
    refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "start" });
    setNavOpen(false);
  };

  return (
    <div style={styles.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;1,8..60,400&family=Inter:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::selection { background: #C9A22744; }
        a { color: inherit; }
        @media (max-width: 760px) {
          .cgv-layout { grid-template-columns: 1fr !important; gap: 0 !important; }
          .cgv-toc { display: none; position: static !important; border-left: none !important; padding-left: 0 !important; border: 1px solid #2A2F36; border-radius: 6px; padding: 16px !important; margin-bottom: 24px; }
          .cgv-toc.open { display: block; }
          .cgv-nav-toggle { display: block !important; }
        }
      `}</style>

      {/* Badge / masthead */}
      <header style={styles.masthead}>
        <div style={styles.badge}>
          <div style={styles.badgeStripe} />
          <div style={styles.badgeContent}>
            <div style={styles.badgeEyebrow}>Document interne · Confidentiel</div>
            <div style={styles.badgeTitle}>CGV&nbsp;GYM</div>
            <div style={styles.badgeSub}>Plateforme de gestion du club</div>
          </div>
          <div style={styles.badgeStripe} />
        </div>
        <h1 style={styles.h1}>Politique de confidentialité</h1>
        <p style={styles.updated}>Dernière mise à jour : 26 août 2026</p>
      </header>

      {/* Mobile nav toggle */}
      <button
        className="cgv-nav-toggle"
        style={styles.navToggle}
        onClick={() => setNavOpen(!navOpen)}
      >
        {navOpen ? "Fermer le sommaire ✕" : "Sommaire ☰"}
      </button>

      <div className="cgv-layout" style={styles.layout}>
        {/* TOC */}
        <nav className={`cgv-toc${navOpen ? " open" : ""}`} style={styles.toc}>
          <div style={styles.tocLabel}>Sommaire</div>
          <ul style={styles.tocList}>
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => jump(s.id)}
                  style={{
                    ...styles.tocItem,
                    ...(active === s.id ? styles.tocItemActive : {}),
                  }}
                >
                  <span style={styles.tocIndex}>{s.label}</span>
                  <span>{s.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Content */}
        <main style={styles.content}>
          <Section id="qui" refs={refs} num="01" title="Qui sommes-nous">
            <p>
              CGV Gym est une plateforme interne de gestion administrative pour
              notre club de sport. Elle est réservée aux administrateurs du
              club, sécurisée par authentification, et n'est accessible à
              aucun autre public.
            </p>
          </Section>

          <Section id="collecte" refs={refs} num="02" title="Données collectées">
            <p>
              Nous collectons les coordonnées des adhérents nécessaires au bon
              fonctionnement du club :
            </p>
            <ul style={styles.ul}>
              <li>Nom et prénom</li>
              <li>Numéro de téléphone et adresse e-mail</li>
              <li>
                Informations liées au suivi des entraînements (programme,
                présence aux séances)
              </li>
            </ul>
          </Section>

          <Section id="usage" refs={refs} num="03" title="Pourquoi ces données">
            <p>
              Ces données sont utilisées exclusivement dans le cadre de
              l'organisation des entraînements et du suivi des adhérents par
              les administrateurs du club :
            </p>
            <ul style={styles.ul}>
              <li>Planification et gestion des séances</li>
              <li>
                Prise de contact en cas de besoin (absence, urgence,
                information sur un cours)
              </li>
              <li>Gestion administrative de l'adhésion</li>
            </ul>
          </Section>

          <Section id="google" refs={refs} num="04" title="Données Google">
            <p>
              Certaines coordonnées d'adhérents peuvent être synchronisées
              avec Google Contacts, à usage strictement interne, afin de
              permettre aux administrateurs de joindre rapidement un adhérent
              depuis leurs outils habituels.
            </p>
            <p>
              Cette utilisation respecte la{" "}
              <em>Google API Services User Data Policy</em>, y compris ses
              exigences de <em>Limited Use</em> : les données issues de
              Google ne sont jamais utilisées à des fins publicitaires, ne
              sont jamais transférées à des tiers, et ne servent en aucun cas
              à des décisions de crédit ou d'assurance. Elles ne sont
              utilisées que pour la fonctionnalité décrite ci-dessus.
            </p>
          </Section>

          <Section id="acces" refs={refs} num="05" title="Qui y a accès">
            <p>
              L'accès à la plateforme est strictement réservé aux
              administrateurs du club, authentifiés individuellement. Il
              n'existe aucun compte adhérent public sur la plateforme.
            </p>
          </Section>

          <Section id="partage" refs={refs} num="06" title="Partage des données">
            <p style={styles.emphasisBox}>
              Vos données ne sont ni vendues, ni partagées avec des tiers, ni
              rendues publiques, sous quelque forme que ce soit. Elles ne
              sont utilisées que par les administrateurs du club, dans le
              cadre strictement défini ci-dessus.
            </p>
          </Section>

          <Section id="securite" refs={refs} num="07" title="Sécurité">
            <p>
              La plateforme est protégée par un système d'authentification
              limitant l'accès aux seuls administrateurs autorisés. Les
              échanges avec le serveur sont chiffrés (HTTPS).
            </p>
          </Section>

          <Section id="conservation" refs={refs} num="08" title="Conservation">
            <p>
              Les données sont conservées pendant toute la durée de
              l'adhésion au club, puis supprimées ou anonymisées dans un
              délai raisonnable après la fin de l'adhésion, sauf obligation
              légale de conservation plus longue.
            </p>
          </Section>

          <Section id="droits" refs={refs} num="09" title="Vos droits">
            <p>
              Conformément au RGPD, vous disposez d'un droit d'accès, de
              rectification, d'effacement et de limitation du traitement de
              vos données. Vous pouvez également vous opposer à leur
              traitement ou demander leur portabilité, en nous contactant à
              l'adresse ci-dessous.
            </p>
          </Section>

          <Section id="contact" refs={refs} num="10" title="Contact">
            <p>
              Pour toute question relative à vos données personnelles ou pour
              exercer vos droits :
            </p>
            <p style={styles.contactLine}>
              contact.cgv.gym@gmail.com
            </p>
            <p style={{ ...styles.small, marginTop: 24 }}>
              Cette politique peut être mise à jour ; la date en haut de page
              indique la dernière révision.
            </p>
          </Section>

          <footer style={styles.footer}>CGV Gym — Usage administratif interne</footer>
        </main>
      </div>
    </div>
  );
}

function Section({ id, refs, num, title, children }) {
  return (
    <section
      id={id}
      ref={(el) => (refs.current[id] = el)}
      style={styles.section}
    >
      <div style={styles.sectionHead}>
        <span style={styles.sectionNum}>{num}</span>
        <h2 style={styles.sectionTitle}>{title}</h2>
      </div>
      <div style={styles.sectionBody}>{children}</div>
    </section>
  );
}

const styles = {
  page: {
    background: "#14171B",
    color: "#EDEAE3",
    minHeight: "100vh",
    fontFamily: "'Source Serif 4', Georgia, serif",
    padding: "0 0 80px",
  },
  masthead: {
    maxWidth: 760,
    margin: "0 auto",
    padding: "56px 24px 32px",
    textAlign: "center",
  },
  badge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    marginBottom: 36,
  },
  badgeStripe: {
    height: 1,
    flex: 1,
    maxWidth: 90,
    background: "linear-gradient(90deg, transparent, #C9A227)",
  },
  badgeContent: {
    border: "1px solid #2A2F36",
    borderRadius: 4,
    padding: "14px 28px",
    background: "#1C2026",
  },
  badgeEyebrow: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#9CA3AD",
    marginBottom: 6,
  },
  badgeTitle: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: 30,
    letterSpacing: "0.06em",
    color: "#C9A227",
    lineHeight: 1,
  },
  badgeSub: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 11,
    color: "#9CA3AD",
    marginTop: 4,
  },
  h1: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: "clamp(32px, 6vw, 48px)",
    letterSpacing: "0.02em",
    fontWeight: 400,
    margin: "0 0 10px",
    color: "#F5F3EF",
  },
  updated: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    color: "#7C838D",
    margin: 0,
  },
  navToggle: {
    display: "none",
    fontFamily: "'Inter', sans-serif",
    background: "#1C2026",
    border: "1px solid #2A2F36",
    color: "#EDEAE3",
    padding: "10px 16px",
    borderRadius: 4,
    margin: "0 auto 12px",
    cursor: "pointer",
    fontSize: 13,
  },
  layout: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "0 24px",
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: 56,
    alignItems: "start",
  },
  toc: {
    position: "sticky",
    top: 32,
    borderLeft: "1px solid #2A2F36",
    paddingLeft: 20,
  },
  tocOpenMobile: {},
  tocLabel: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 10,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "#7C838D",
    marginBottom: 14,
  },
  tocList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  tocItem: {
    display: "flex",
    gap: 10,
    alignItems: "baseline",
    width: "100%",
    background: "none",
    border: "none",
    color: "#9CA3AD",
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    textAlign: "left",
    padding: "6px 8px",
    borderRadius: 3,
    cursor: "pointer",
  },
  tocItemActive: {
    color: "#F5F3EF",
    background: "#1C2026",
  },
  tocIndex: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: 12,
    color: "#C9A227",
    letterSpacing: "0.04em",
  },
  content: {
    minWidth: 0,
  },
  section: {
    padding: "36px 0",
    borderBottom: "1px solid #21252B",
  },
  sectionHead: {
    display: "flex",
    alignItems: "baseline",
    gap: 14,
    marginBottom: 14,
  },
  sectionNum: {
    fontFamily: "'Bebas Neue', sans-serif",
    fontSize: 15,
    color: "#C9A227",
    letterSpacing: "0.04em",
  },
  sectionTitle: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 20,
    fontWeight: 600,
    color: "#F5F3EF",
    margin: 0,
  },
  sectionBody: {
    fontSize: 16,
    lineHeight: 1.7,
    color: "#D8D4CC",
    maxWidth: 620,
  },
  ul: {
    margin: "8px 0 0",
    paddingLeft: 20,
    lineHeight: 1.8,
  },
  emphasisBox: {
    borderLeft: "3px solid #C9A227",
    paddingLeft: 16,
    fontStyle: "italic",
    color: "#F5F3EF",
  },
  contactLine: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 15,
    color: "#C9A227",
  },
  small: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    color: "#7C838D",
  },
  footer: {
    fontFamily: "'Inter', sans-serif",
    fontSize: 11,
    color: "#4E545C",
    textAlign: "center",
    paddingTop: 40,
    letterSpacing: "0.04em",
  },
};